import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

// One bell for the whole portal: task assignments, policy updates, training.
// The badge counts UNREAD items; opening one marks it read and jumps to the
// page that can action it.
const KIND = {
  task:             { icon: '✓',  label: 'Task',     colour: 'var(--accent)' },
  policy:           { icon: '📋', label: 'Policy',   colour: '#B26A00' },
  training:         { icon: '🎓', label: 'Training', colour: 'var(--green)' },
  training_request: { icon: '🙋', label: 'Training request', colour: 'var(--red)' },
};

// A push subscription's key arrives as base64url; the browser wants bytes.
const toBytes = b64 => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

// ── Phone alerts ────────────────────────────────────────────────────────────
// Turning this on subscribes THIS device. Everything that reaches the bell then
// reaches the phone too — useful while email is unavailable.
function PhoneAlerts() {
  const [state, setState] = useState('checking');   // off | on | unsupported | denied | busy
  const [msg, setMsg] = useState('');

  useEffect(() => { (async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return setState('unsupported');
    const r = await api.get('/push/key').catch(() => null);
    if (!r || !r.data.enabled) return setState('unsupported');
    if (Notification.permission === 'denied') return setState('denied');
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && await reg.pushManager.getSubscription();
    setState(sub ? 'on' : 'off');
  })().catch(() => setState('unsupported')); }, []);

  const enable = async () => {
    setState('busy'); setMsg('');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'denied' : 'off'); return; }
      const { data } = await api.get('/push/key');
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true, applicationServerKey: toBytes(data.key) });
      await api.post('/push/subscribe', sub.toJSON());
      setState('on'); setMsg('This device will now get alerts.');
    } catch (e) { setState('off'); setMsg('Could not turn them on here.'); }
  };

  const disable = async () => {
    setState('busy');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setState('off'); setMsg('');
    } catch (e) { setState('on'); }
  };

  const test = async () => {
    try { await api.post('/push/test'); setMsg('Sent — check your notifications.'); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not send a test.'); }
  };

  if (state === 'checking' || state === 'unsupported') return null;

  return (
    <div style={{ padding:'9px 12px', borderBottom:'1px solid var(--border)',
      background:'var(--bg3)', fontSize:12 }}>
      {state === 'denied' ? (
        <span style={{ color:'var(--text2)' }}>
          Alerts are blocked for this site in your browser settings.
        </span>
      ) : state === 'on' ? (
        <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ color:'var(--green)', fontWeight:600 }}>🔔 Alerts on for this device</span>
          <button className="btn btn-sm btn-secondary" style={{ marginLeft:'auto' }} onClick={test}>Test</button>
          <button className="btn btn-sm btn-secondary" onClick={disable}>Turn off</button>
        </div>
      ) : (
        <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ color:'var(--text2)' }}>Get these on your phone even when the portal is closed.</span>
          <button className="btn btn-sm btn-primary" style={{ marginLeft:'auto' }}
            disabled={state === 'busy'} onClick={enable}>
            {state === 'busy' ? 'Working…' : 'Turn on'}
          </button>
        </div>
      )}
      {msg && <div style={{ marginTop:5, color:'var(--text2)' }}>{msg}</div>}
    </div>
  );
}

const ago = d => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

export default function NotificationBell({ onOpen }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  const load = useCallback(() => {
    api.get('/notifications').then(r => setData(r.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const openItem = async n => {
    setOpen(false);
    if (!n.read_at) await api.post(`/notifications/${n.id}/read`).catch(()=>{});
    load();
    onOpen?.(n);
  };
  const readAll = async () => { await api.post('/notifications/read-all').catch(()=>{}); load(); };

  return (
    <div ref={box} style={{ position:'relative' }}>
      <button onClick={() => setOpen(o => !o)} title="Notifications"
        style={{ position:'relative', background:'none', border:'none', cursor:'pointer',
                 fontSize:19, lineHeight:1, padding:'4px 6px' }}>
        🔔
        {data.unread > 0 && (
          <span style={{ position:'absolute', top:-2, right:-2, minWidth:17, height:17, padding:'0 4px',
            borderRadius:9, background:'var(--red)', color:'#fff', fontSize:10.5, fontWeight:700,
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            {data.unread > 9 ? '9+' : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="card" style={{ position:'absolute', right:0, top:34, width:360, maxHeight:430,
          overflowY:'auto', zIndex:200, padding:0, boxShadow:'0 8px 28px rgba(0,0,0,0.18)' }}>
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center',
            padding:'10px 12px', borderBottom:'1px solid var(--border)', position:'sticky', top:0, background:'var(--bg2)' }}>
            <b style={{ fontSize:13 }}>Notifications</b>
            {data.unread > 0 && <button className="btn btn-sm btn-secondary" onClick={readAll}>Mark all read</button>}
          </div>

          <PhoneAlerts />

          {!data.items.length && (
            <div style={{ padding:'22px 14px', textAlign:'center', fontSize:13, color:'var(--text2)' }}>
              Nothing new. 🎉
            </div>
          )}

          {data.items.map(n => {
            const k = KIND[n.kind] || { icon: '•', label: n.kind, colour: 'var(--text2)' };
            return (
              <button key={n.id} onClick={() => openItem(n)}
                style={{ display:'block', width:'100%', textAlign:'left', border:'none', cursor:'pointer',
                  padding:'10px 12px', borderBottom:'1px solid var(--border)',
                  background: n.read_at ? 'transparent' : 'var(--bg3)',
                  borderLeft:`3px solid ${n.read_at ? 'transparent' : k.colour}` }}>
                <div className="flex-gap" style={{ gap:6, alignItems:'center', marginBottom:2, flexWrap:'wrap' }}>
                  <span style={{ fontSize:12 }}>{k.icon}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:k.colour, letterSpacing:'.03em' }}>
                    {k.label.toUpperCase()}
                  </span>
                  <span style={{ fontSize:10.5, color:'var(--text2)', marginLeft:'auto' }}>{ago(n.created_at)}</span>
                </div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{n.title}</div>
                {n.body && <div style={{ fontSize:12, color:'var(--text2)' }}>{n.body}</div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
